import React, { useEffect, useRef } from 'react';
import { Layout } from 'components/layout';
import { isWindowDefined } from 'helpers/utils';
import Indexes, { IndexContent } from './indexes';
import * as styles from './indexed-content.module.scss';

type IndexContentType = {
  title: string;
  subtitle: string;
  lastUpdated: string;
  content: string;
  contentClassName?: string;
};

export const generateIndexedContent = (
  { content, filterTopPicks }: { content: string, filterTopPicks?: string[] },
): IndexContent[] => {
  let headings: Element[] = [];
  let result: IndexContent[] = [];

  if (isWindowDefined) {
    headings = Array.from(
      new DOMParser().parseFromString(content, 'text/html').querySelectorAll('h2'),
    );
  }

  result = headings.map((heading: Element, index) => ({
    iTitle: heading.textContent || '',
    id: index,
  }));

  if (filterTopPicks) {
    result = result.filter(
      (item) => filterTopPicks.find((p) => item.iTitle.toLowerCase().includes(p.toLowerCase())),
    );
  }

  return result;
};

const IndexedContent = ({
  title, subtitle, lastUpdated, content, contentClassName,
}: IndexContentType) => {
  const contentRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    // Scroll to the section based on the URL hash
    if (window.location.hash) {
      const targetElement = document.getElementById(window.location.hash.substring(1));
      if (targetElement) {
        targetElement.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, []);

  const indexedContent = generateIndexedContent({ content });

  const handleIndexClick = (index: number) => {
    if (contentRef.current) {
      const targetElement = contentRef.current.querySelectorAll('h2')[index];
      if (targetElement) {
        targetElement.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  return (
    <Layout>
      <div className={styles.genericContainer}>
        <div className={styles.gridContent}>
          <div className={styles.mainContent}>
            <h1 className={styles.title}>{title}</h1>
            <p className={styles.subtitle}>{subtitle}</p>
            <p className={styles.lastUpdated}>{lastUpdated}</p>
            <div
              ref={contentRef}
              className={contentClassName ?? styles.contentStyle}
              dangerouslySetInnerHTML={{ __html: content }}
            />
          </div>
          <Indexes indexes={indexedContent} onIndexClick={handleIndexClick} title={title} />
        </div>
      </div>
    </Layout>
  );
};

export default IndexedContent;
